import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { environment } from '../../../../environments/environment';

declare const google: any;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  loading = false;
  errorMessage = '';
  showResendVerification = false;
  resendingVerification = false;
  resendSuccessMessage = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.initializeGoogleSignIn();
  }

  private initializeGoogleSignIn(): void {
    // Wait for Google script to load
    if (typeof google !== 'undefined') {
      google.accounts.id.initialize({
        client_id: environment.googleClientId,
        callback: (response: any) => this.handleGoogleLogin(response)
      });

      google.accounts.id.renderButton(
        document.getElementById('googleSignInButton'),
        {
          theme: 'outline',
          size: 'large',
          width: '100%',
          text: 'continue_with'
        }
      );
    } else {
      // Retry after a short delay if Google script not loaded yet
      setTimeout(() => this.initializeGoogleSignIn(), 100);
    }
  }

  handleGoogleLogin(response: any): void {
    this.loading = true;
    this.errorMessage = '';

    this.authService.loginWithGoogle(response.credential).subscribe({
      next: (apiResponse) => {
        console.log('Google login successful:', apiResponse);
        this.router.navigate(['/products']);
      },
      error: (error) => {
        console.error('Google login error:', error);
        this.errorMessage = error.error?.error?.message || 'Google login failed. Please try again.';
        this.loading = false;
      }
    });
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.showResendVerification = false;
    this.resendSuccessMessage = '';

    this.authService.login(this.loginForm.value).subscribe({
      next: (response) => {
        console.log('Login successful:', response);
        this.router.navigate(['/products']);
      },
      error: (error) => {
        console.error('Login error:', error);
        this.errorMessage = error.error?.error?.message || 'Login failed. Please try again.';

        // Check if error is EMAIL_NOT_VERIFIED
        if (error.error?.error?.code === 'EMAIL_NOT_VERIFIED') {
          this.showResendVerification = true;
        }

        this.loading = false;
      }
    });
  }

  resendVerificationEmail(): void {
    this.resendingVerification = true;
    this.resendSuccessMessage = '';
    this.errorMessage = '';

    const email = this.loginForm.value.email;

    this.authService.resendVerificationEmail(email).subscribe({
      next: (response) => {
        console.log('Verification email resent:', response);
        this.resendSuccessMessage = 'Verification email sent! Please check your inbox.';
        this.showResendVerification = false;
        this.resendingVerification = false;
      },
      error: (error) => {
        console.error('Resend verification error:', error);
        this.errorMessage = error.error?.error?.message || 'Failed to resend verification email.';
        this.resendingVerification = false;
      }
    });
  }

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }
}
